/**
 * SidebarMenu Component
 * A React component that provides responsive navigation sidebar functionality
 * Features separate desktop and mobile layouts with animated transitions
 */

import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  XMarkIcon,
  HomeIcon,
  InformationCircleIcon,
  StarIcon,
  WalletIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/solid";

/**
 * Sidebar navigation menu component with responsive design
 * Includes click-outside detection for mobile view and environment indicator
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Current state of the mobile sidebar
 * @param {Function} props.toggleSidebar - Function to toggle the sidebar state
 * @returns {JSX.Element} Rendered sidebar navigation menu
 */
function SidebarMenu({ isOpen, toggleSidebar }) {
  /** Reference to the sidebar element for click-outside detection */
  const sidebarRef = useRef(null);

  /**
   * Effect hook to handle clicks outside the sidebar on mobile
   * Closes the sidebar when clicking outside its bounds
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        toggleSidebar();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, toggleSidebar]);

  /**
   * Component render section
   * Renders three main parts:
   * 1. Desktop sidebar (hidden on mobile)
   * 2. Mobile sidebar (hidden on desktop)
   * 3. Mobile close button ("Pulsante per chiudere la sidebar su mobile")
   */
  return (
    <>
      {/* Desktop sidebar */}
      <div
        className="hidden md:block w-64 p-4 min-h-screen
                backdrop-blur-lg bg-opacity-70 border-r border-sidebar-border"
      >
        <div className="text-lg font-bold mb-4">
          <h2>Menu</h2>
          {process.env.REACT_APP_ENV !== "prod" && (
            <h2>Env: {process.env.REACT_APP_ENV}</h2>
          )}
        </div>
        <nav className="mt-11">
          <Link
            to="/"
            className="flex items-center mb-2 p-2 rounded transition-colors duration-300"
          >
            <HomeIcon className="w-5 h-5 mr-2" />
            Home
          </Link>
          <Link
            to="/collections"
            className="flex items-center mb-2 p-2 rounded transition-colors duration-300 "
          >
            <StarIcon className="w-5 h-5 mr-2" />
            Collections
          </Link>
          <Link
            to="/claim"
            className="flex items-center mb-2 p-2 rounded transition-colors duration-300 "
          >
            <WalletIcon className="w-5 h-5 mr-2" />
            Test claim
          </Link>
          <Link
            to="/nft-gallery"
            className="flex items-center mb-2 p-2 rounded transition-colors duration-300 "
          >
            <GlobeAltIcon className="w-5 h-5 mr-2" />
            My assets
          </Link>
          <Link
            to="/about"
            className="flex items-center mb-2 p-2 rounded transition-colors duration-300 "
          >
            <InformationCircleIcon className="w-5 h-5 mr-2" />
            About
          </Link>
        </nav>
      </div>

      {/* Mobile sidebar */}
      <div
        ref={sidebarRef}
        className={`md:hidden fixed top-0 left-0 p-2 min-h-screen w-72 
                backdrop-blur-lg bg-opacity-70 z-50 transform transition-transform duration-300 ease-in-out 
                border-r border-sidebar-border ${isOpen ? "translate-x-0" : "-translate-x-full"}
            `}
      >
        <div className="text-lg font-bold mb-4">
          <h2>Menu</h2>
        </div>
        <nav className="mt-11">
          <Link
            to="/"
            className="flex items-center mb-2 p-2 rounded transition-colors duration-300 "
            onClick={toggleSidebar}
          >
            <HomeIcon className="w-5 h-5 mr-2" />
            Home
          </Link>
          <Link
            to="/about"
            className="flex items-center mb-2 p-2 rounded transition-colors duration-300 "
            onClick={toggleSidebar}
          >
            <InformationCircleIcon className="w-5 h-5 mr-2" />
            About
          </Link>
        </nav>
      </div>

      {/* Mobile close button */}
      <div
        className={`
                md:hidden fixed top-2 z-50 transition-all duration-300 ease-in-out transform 
                ${isOpen ? "translate-x-64 ms-0" : "translate-x-0 ms-3"}    
            `}
      >
        <button className="" onClick={toggleSidebar}>
          {isOpen && <XMarkIcon className="w-6 h-6" />}
        </button>
      </div>
    </>
  );
}

export default SidebarMenu;
